import React, { useMemo, useCallback } from 'react';
import { Marker, InfoBox } from '@react-google-maps/api';
import dayjs from 'dayjs';
import { useCookies } from 'react-cookie';
import { Button } from 'src/components';
import { IconClose } from 'src/icons';
import { useCreateModal, useEditData } from 'src/stores';
import { picCamera } from 'src/images'
import { Camera } from 'src/pages/Home/Home';
import styles from './CameraMarker.module.scss';

type Props = {
  camera: Camera;
  activeCameraId: number | null;
  c_activeMarker: (v: Camera) => void;
  c_closeInfoBox: () => void;
  photo_count: any[];
};

// InfoBox オプション値
const infoBoxOptions = {
  disableAutoPan: true,
  alignBottom: true,
  maxWidth: 270,
  boxClass: styles.infobox,
};

// 備考制限設定
const l_limit = `${process.env.REACT_APP_HOME_REMARKS_LIMIT_LINE}`+"em";

/**
 * CameraMarker コンポーネント
 * @returns React.ReactNode
 */
const CameraMarker: React.FC<Props> = ({ camera, activeCameraId, c_activeMarker, c_closeInfoBox, photo_count }) => {
  /**
   * 害獣座標をメモ化
   */
  const latlng = useMemo(() => new google.maps.LatLng({
    lat: Number(camera.camera_latitude),
    lng: Number(camera.camera_longitude),
  }), [camera]);

  const [cookie] = useCookies();

  /**
   * InfoBox を表示するハンドラ
   * @example
   * <Marker onClick={handleMarkerClick} ... />
   */
  const handleMarkerClick = useCallback(() => {
    c_activeMarker(camera);
  }, [c_activeMarker, camera]);

/**
  * 情報登録モーダルフラグ
  */
  const [createCameraModalOpen] = useCreateModal(state => [state.createCameraModalOpen]);

/**
  * 編集モーダル開閉時のデータ格納ストア
  */
  const [setEditCameraData] = useEditData(state => [state.setEditCameraData]);

/**
  * 害獣情報修正モーダル を表示するハンドラ
  * @example
  * <Button onClick={handleEditModal} ... />
  */
  const handleEditModal = useCallback(() => {
    setEditCameraData(camera);
    createCameraModalOpen();
  }, [camera]);

  //クリップボードにコピー関数
const copyToClipboard = useCallback(async () => {
  const url = window.location.origin +"/?corporate="+camera.client_code+"&S_lat="+camera.camera_latitude+"&S_lon="+camera.camera_longitude
  await global.navigator.clipboard.writeText(url);
  alert("URLをコピー")
},[camera]);

  return (
    <>
      <Marker
        position={latlng}
        icon={{
          // icon: leaf - green.png,
          url: picCamera,
          // anchor: new google.maps.Point(20, 20),
        }}
        onClick={handleMarkerClick}
        onRightClick={copyToClipboard}/*230915追記*/
        onDblClick={copyToClipboard}
        zIndex={camera.camera_id === activeCameraId ? 100 : 0}
      />
      {camera.camera_id === activeCameraId && (
        <InfoBox
          position={latlng}
          options={{
            ...infoBoxOptions,
            pixelOffset: new window.google.maps.Size(-135, -25),
          }}
        >
          <div className={styles.overview}>
            <button
              className={styles.closeButton}
              aria-label="詳細ボックスを閉じる"
              onClick={c_closeInfoBox}
            >
              <IconClose />
            </button>
            <ul>
              <li>{camera.camera_id && `カメラID: ${camera.camera_id}`}</li>
              <li>{camera.camera_direction && `撮影方向: ${camera.camera_direction}`}</li>
              <li>{camera.trap_id && `撮影罠のID: ${camera.trap_id}`}</li>
              <li>{camera.camera_start && `撮影開始日: ${dayjs(camera.camera_start).format('YYYY年MM月DD日')}`}</li>
              <li>{camera.camera_stop && `撮影終了日: ${dayjs(camera.camera_stop).format('YYYY年MM月DD日')}`}</li>
              <li className={styles.remarks_hidden} style={{maxHeight: l_limit}}>{camera.camera_remarks && `備考: ${camera.camera_remarks}`}</li>
              <li>{photo_count.length !== 0 && `撮影頭数: ${photo_count[0].photo_count}頭`}</li>
            </ul>
            <div className={`${cookie?.verminData?.user_kind_id > 2 ? styles.row : styles.row2}`}>
              <Button onClick={handleEditModal} disabled={!cookie?.verminData?.user_kind_id || camera.client_code!==cookie?.verminData?.client_code || cookie?.verminData?.user_kind_id === 1 || (cookie?.verminData?.user_kind_id === 2 && cookie?.verminData?.user_info_id !== camera.update_user_id)}>
                情報編集
              </Button>
            </div>
          </div>
        </InfoBox>
      )}
    </>
  );
};

export default React.memo(CameraMarker);
